import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { isUserLoggedIn, roundCredit } from '../helpers'
import { getSubscription, Subscription } from '../service/subscription'

interface SubscriptionContextValue {
  /** Raw subscription document, or null until the first fetch resolves. */
  subscription: Subscription | null
  /** Rounded point balance, ready to render ('' while unknown). */
  credit: string
  /** 'trial' | the paid plan type (`subscription.type`) | '' */
  planType: string
  /** Trial days left, or the paid billing period ('' while unknown). */
  planPeriod: string
  loading: boolean
  /**
   * Force a re-fetch. You rarely need to call this by hand: the global fetch
   * wrapper in `App.tsx` dispatches `subscriptionUpdated` after every
   * credit-affecting call (purchase and service usage alike), which the provider
   * already listens for.
   */
  refresh: () => Promise<void>
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined)

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [loading, setLoading] = useState(false)
  const isMounted = useRef(true)

  const refresh = useCallback(async () => {
    if (!isUserLoggedIn()) return
    setLoading(true)
    try {
      const data = await getSubscription()
      if (isMounted.current) setSubscription(data)
    } catch {
      // getSubscription already surfaces failures through the global fetch handler
    } finally {
      if (isMounted.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    isMounted.current = true
    refresh()

    const onSubscriptionUpdated = () => refresh()
    window.addEventListener('subscriptionUpdated', onSubscriptionUpdated)

    return () => {
      isMounted.current = false
      window.removeEventListener('subscriptionUpdated', onSubscriptionUpdated)
    }
  }, [refresh])

  const credit =
    roundCredit(subscription?.isTrial ? subscription?.trialPointsLimit : subscription?.credit) ?? ''

  const planType = subscription?.isTrial ? 'trial' : (subscription?.type ?? '')

  const planPeriod = subscription?.isTrial
    ? String(subscription?.trialDaysLimit ?? '')
    : (subscription?.period ?? '')

  return (
    <SubscriptionContext.Provider
      value={{ subscription, credit, planType, planPeriod, loading, refresh }}
    >
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext)
  if (!ctx) {
    throw new Error('useSubscription must be used within a <SubscriptionProvider>')
  }
  return ctx
}
